import {
  useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ApiError, getReport, queueGrade, release, reviewGrade, setSubmissionProfile,
  type Cite, type LostItem, type RequirementResult, type ReviewCriterionInput, type Report,
} from "../api";
import { useShell } from "../Layout";
import { FILTER_OF_STATUS, routes } from "../routes";
import { tracked, useAsync } from "../useAsync";
import { FILTER_NOUN } from "./marking";
import { reasonLabel } from "../reasons";
import { CriterionTests, TeacherTestsReport } from "./TeacherTests";
import {
  Badge, Card, CardBody, CopyGlyph, DASH, ErrorBox, Markdown, MarkdownInline, Money, State, When,
  Why,
} from "../ui";
import {
  moveOf, moveRange, NOT_READ, NOT_READ_WHY, resetDuring, runMinutes, runTokens, tokenCount,
  tokenTotal, UPPER,
} from "../usage";
import { RunLogView, turnsOf } from "../runlog";

/**
 * The only chart in the app, and it makes one claim: how much of this mark the agent stood
 * behind with evidence, and how much it did not.
 *
 * 🔴 IT USED TO CLAIM SOMETHING THAT IS NO LONGER TRUE. It split the criteria on
 * `evidence === "test"` — measured by a reference suite versus judged by the agent — and
 * printed "N of M by the reference suite". There are no reference suites; every criterion is
 * judged. `evidence` is not even a string any more, so the split was always empty and the bar
 * always read "0 of 0 by the reference suite", in green, on every grade.
 *
 * The honest split now is EVIDENCED versus not: a criterion the agent cited a resolving file
 * for, against one it scored with nothing behind it. That is a real distinction a teacher
 * should see, and it is the one the scorer actually records.
 */
function Meter({ per }: { per: Report["per_criterion"] }) {
  const sum = (rows: Report["per_criterion"], f: "earned" | "weight") =>
    rows.reduce((a, c) => a + (Number(c[f]) || 0), 0);
  const cited = per.filter((c) => (c.evidence?.length ?? 0) > 0);
  const bare = per.filter((c) => (c.evidence?.length ?? 0) === 0);
  const total = sum(per, "weight");
  if (!total) return null;
  return (
    <span className="rp-meter">
      <span>
        {sum(cited, "earned")} of {sum(cited, "weight")} with a citation ·{" "}
        {sum(bare, "earned")} of {sum(bare, "weight")} without
      </span>
      <span className="meter">
        <i style={{ width: `${(sum(cited, "earned") / total) * 100}%`,
                    background: "var(--tb-ok)" }} />
        <i style={{ width: `${(sum(bare, "earned") / total) * 100}%`,
                    background: "var(--tb-flag)" }} />
      </span>
    </span>
  );
}

/** `path:line` citations become links into the student's own repository at the graded commit.
 *
 * 🔴 THE COMMIT, NOT THE BRANCH. A branch link shows the file as it is NOW, and a student who
 * pushed after the run would show a teacher different code under the line the grader cited. The
 * commit is what was graded; the branch is only the fallback for a grade made before runs were
 * pinned to one. */
/** The path inside the student's repository, from the path the agent cited.
 *
 * 🔴 `src/` IS THE AGENT'S MOUNT, NOT A DIRECTORY IN THE REPOSITORY. The jail clones the
 * submission to `./src` and works from its parent, so the only path the agent can honestly type
 * starts `src/` — `grade_submission.sh` says exactly that, and `finalise.py` mirrors the mount
 * so citations resolve. Both are right. This is the one layer that has to undo it, because a
 * GitHub blob path is relative to the repository root and the mount is not part of it.
 *
 * ⚠️ EXACTLY ONE SEGMENT, AND ONLY THE FIRST. A Rust crate and this very TypeScript submission
 * both genuinely contain a `src/`, so the agent writes `src/src/lib.rs` and the repository holds
 * `src/lib.rs`. Stripping more than one would point at a file that is not there; stripping none
 * is the defect this function exists for — measured in production, every citation link 404ing
 * with "the <sha> branch does not contain the path src/src/...".
 *
 * ⚠️ A PATH WITHOUT THE PREFIX IS LEFT ALONE. It never resolved against the verification clone,
 * so its criterion already scored zero — but a grade stored before the contract was stated still
 * renders its evidence, and mangling it further would help nobody.
 *
 * 🔴 DO NOT "fix" this in `finalise.py` or in the jail instead. Those two must keep the mount, or
 * `src/lib.rs` becomes ambiguous between the agent's view and the student's own layout — and the
 * wrong reading resolves to a real file, which is the worst kind of wrong. */
export function repoPath(file: string): string {
  const rest = file.slice(4);
  return file.startsWith("src/") && rest ? rest : file;
}

/** The deepest submodule holding a repository path, or nothing. A folder that merely begins
 *  with a submodule's name is not inside it. */
function submoduleOf(subs: Report["repo"]["submodules"], inner: string) {
  return (subs ?? []).filter((m) => inner.startsWith(`${m.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export function citeHref(repo: Report["repo"], citation: string): string | null {
  const m = /^(.+?):(\d+)(?:-(\d+))?$/.exec(citation.trim());
  const line = m?.[2];
  const file = (m?.[1] ?? citation).trim();
  if (!file || !repo.url.startsWith("https://github.com/")) return null;
  const ref = repo.commit || repo.ref || "HEAD";
  // ⚠️ THE MOUNT COMES OFF BEFORE THE SUBMITTED DIRECTORY GOES ON. For a `.../tree/<ref>/<dir>`
  // submission the agent's `./src` IS that directory, so both have to happen and in this order.
  // 🔴 A FILE INSIDE A SUBMODULE LIVES IN THAT SUBMODULE'S REPOSITORY (2026-10-07), named by its
  // own commit, never the superproject's — see `submoduleOf`.
  const inner = [repo.subpath, repoPath(file)].filter(Boolean).join("/");
  const sm = submoduleOf(repo.submodules, inner);
  const [base, at, path] = sm ? [sm.url, sm.sha, inner.slice(sm.path.length + 1)]
    : [repo.url, ref, inner];
  if (!base.startsWith("https://github.com/")) return null;
  return `${base}/blob/${encodeURIComponent(at)}/${path}` + (line ? `#L${line}` : "");
}

/** A submodule's own tree at the commit the student's repository pins. */
export function submoduleHref(m: { url: string; sha: string }): string {
  return m.url.startsWith("https://github.com/") ? `${m.url}/tree/${m.sha}` : m.url;
}

/** The submitted directory at the graded commit, where the host has such a page. */
export function treeHref(repo: Report["repo"]): string {
  if (!repo.commit || !repo.url.startsWith("https://github.com/")) return repo.url;
  return `${repo.url}/tree/${repo.commit}` + (repo.subpath ? `/${repo.subpath}` : "");
}


type Verdict = { verdict: "" | "agree" | "adjust"; points: string; reason: string; note: string };

/** The draft a review starts from: the review in force, or nothing decided at all.
 *
 * 🔴 NOTHING IS PRE-TICKED. Starting every criterion on Agree would make agreement the thing a
 * Save records by default — and agreement between teacher and agent is the measurement every
 * later change to the grader is judged by. A verdict nobody chose is not a verdict. */
function startingDraft(r: Report): Record<string, Verdict> {
  const held = new Map((r.review?.criteria ?? []).map((v) => [v.id, v]));
  return Object.fromEntries(r.per_criterion.map((c) => {
    const v = held.get(c.id);
    return [c.id, v ? { verdict: v.verdict, points: String(v.points), reason: v.reason,
                        note: v.note }
                    : { verdict: "", points: "", reason: "", note: "" }];
  }));
}

/** The number one criterion contributes, or null while it is undecided or unreadable. */
function scored(c: Report["per_criterion"][number], v: Verdict): number | null {
  if (v.verdict === "agree") return c.result === "judged" ? Number(c.earned) : null;
  if (v.verdict !== "adjust" || v.points.trim() === "") return null;
  const n = Number(v.points);
  return Number.isFinite(n) ? n : null;
}

// ── the grade report explains itself (owner, 2026-09-29) ─────────────────────────
//
// Owner: "the presentation of the reports should be more friendly and verbose being optional …
// markdown text … per file information if makes sense … explain why not 100, where it fails,
// separated, well organized". The approved mockup is layout A:
// https://claude.ai/artifact/3SgC51KvYkZ6iBMu9bWi3E — design in
// docs/plans/2026-09-29-grade-report-design.md.
// 🔴 EVERY CLASS HERE CARRIES `rp-`: `.q`, `.n` and `.note` already mean something else in
// index.css, and a bare short name picked one of them up in the first mockup render.

type Crit = Report["per_criterion"][number];

/** A number as the report writes it: whole when it is whole, else to two places. */
const pts = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
/** U+2212, the minus sign — a hyphen reads as a dash beside a number. */
const minus = (n: number) => `−${pts(n)}`;

/** The directory and the file name of a cited path, as the student's repository has it. */
function splitPath(file: string): [string, string] {
  const p = repoPath(file);
  const i = p.lastIndexOf("/");
  return i < 0 ? ["", p] : [p.slice(0, i + 1), p.slice(i + 1)];
}

function CitePath({ r, file }: { r: Report; file: string }) {
  const [dir, name] = splitPath(file);
  const href = citeHref(r.repo, file);
  const body = <><span className="rp-dir">{dir}</span>{name}</>;
  return href ? <a href={href} target="_blank" rel="noreferrer">{body}</a> : <span>{body}</span>;
}

/** One cited line: its number (a link into the repository at the graded commit), the text
 *  there, and — when there is one — what it was cited for. */
function CiteLine({ r, file, line, quote, caption, tone }: {
  r: Report; file: string; line: number; quote: string; caption?: ReactNode;
  tone?: "lost" | "would";
}) {
  const href = citeHref(r.repo, `${file}:${line}`);
  return (
    <div className={tone === "lost" ? "rp-line rp-line-lost"
                    : tone === "would" ? "rp-line rp-line-would" : "rp-line"}>
      {href ? <a className="rp-ln" href={href} target="_blank" rel="noreferrer">{line}</a>
            : <span className="rp-ln">{line}</span>}
      <code className="rp-q">{quote}</code>
      {caption && <div className={tone === "lost" ? "rp-for rp-for-lost" : "rp-for"}>{caption}</div>}
    </div>);
}

type FileLine = { cid: string; line: number; quote: string; shows?: string; lost?: LostItem;
                  counted: boolean };
type FileEntry = { file: string; lines: FileLine[]; cids: string[]; lostCounted: boolean;
                   lostWould: boolean; note?: string };

/**
 * Every file a grade cites, with what each cited line was cited for.
 *
 * ⚠️ BUILT FROM THE CITATIONS THE GRADE ALREADY CARRIES — its evidence and its lost points — and
 * the grader's optional note per file. A line cited both as evidence and for lost points appears
 * once, as the lost one, because that is the reading a teacher is checking.
 * ⚠️ FILES THAT LOST POINTS COME FIRST, then those an advisory criterion would have cost, then
 * the rest — the order a teacher checking the mark reads them in.
 */
export function filesOf(r: Report, only?: string): FileEntry[] {
  const by = new Map<string, FileEntry>();
  const add = (file: string, fl: FileLine) => {
    let f = by.get(file);
    if (!f) {
      f = { file, lines: [], cids: [], lostCounted: false, lostWould: false };
      by.set(file, f);
    }
    if (!f.cids.includes(fl.cid)) f.cids.push(fl.cid);
    if (fl.lost) {
      if (fl.counted) f.lostCounted = true; else f.lostWould = true;
    }
    const same = f.lines.findIndex((x) => x.cid === fl.cid && x.line === fl.line);
    if (same < 0) f.lines.push(fl);
    else if (fl.lost && !f.lines[same]!.lost) f.lines[same] = fl;
  };
  for (const c of r.per_criterion) {
    if (only !== undefined && c.id !== only) continue;
    const counted = c.result !== "advisory";
    for (const e of c.evidence ?? []) {
      add(e.file, { cid: c.id, line: e.line, quote: e.quote, shows: e.shows, counted });
    }
    for (const it of c.lost ?? []) {
      for (const e of it.evidence ?? []) {
        add(e.file, { cid: c.id, line: e.line, quote: e.quote, shows: e.shows, lost: it, counted });
      }
    }
  }
  const notes = new Map((r.file_notes ?? []).map((n) => [n.file, n.note]));
  const rank = (f: FileEntry) => (f.lostCounted ? 0 : f.lostWould ? 1 : 2);
  const out = [...by.values()];
  for (const f of out) {
    f.note = notes.get(f.file);
    f.lines.sort((a, b) => a.line - b.line || a.cid.localeCompare(b.cid));
  }
  return only !== undefined ? out
    : out.sort((a, b) => rank(a) - rank(b) || repoPath(a.file).localeCompare(repoPath(b.file)));
}

/** What one cited line was cited for, in words. */
function forWhat(l: FileLine, withId: boolean): string {
  const id = withId ? `${l.cid} · ` : "";
  if (l.lost) {
    return `${id}${l.counted ? "cited for" : "would have cost"} ${minus(l.lost.points)}: `
      + l.lost.what;
  }
  return l.shows ? `${id}${l.shows}` : withId ? l.cid : "";
}

type WhyLine = { key: string; points: number | null; sign: "-" | "+"; what: string;
                 more?: string; cites?: Cite[] };

/** The lines that explain one criterion's missing points, in the order they are read. */
function whyLines(c: Crit, v?: NonNullable<Report["review"]>["criteria"][number]): WhyLine[] {
  const out: WhyLine[] = [];
  const advisory = c.result === "advisory";
  const gap = advisory
    ? (c.advisory_points == null ? 0 : (c.advisory_weight ?? 0) - c.advisory_points)
    : c.weight - (c.earned ?? 0);
  if (c.lost === undefined) {
    // ⚠️ A GRADE MADE BEFORE 2026-09-29. Nothing itemised it, and saying so beats inventing a
    // breakdown; the reasoning on the card is where the answer is.
    if (!advisory && c.result === "absent" && gap > 0) {
      out.push({ key: "absent", points: gap, sign: "-",
                 what: "The grader never judged this criterion." });
    } else if (!advisory && gap > 0) {
      out.push({ key: "legacy", points: gap, sign: "-",
                 what: "Not itemised: this grade was made before the grader listed where "
                   + "points went.",
                 more: "Its reasoning, below, says why." });
    }
  } else {
    c.lost.forEach((it, i) => out.push({ key: `lost-${i}`, points: it.points, sign: "-",
                                         what: it.what, more: it.detail, cites: it.evidence }));
    // 🔴 EXTRA CREDIT IS A LINE OF ITS OWN (2026-10-07): it gave back some of what the required
    // tasks lost, and without it the lines above would add up to more than the mark shows.
    if (c.credit) {
      out.push({ key: "credit", points: c.credit.points, sign: "+", what: c.credit.reason });
    }
    if (c.lost_console) {
      out.push({ key: "console", points: c.lost_console.points, sign: "-",
                 what: c.lost_console.reason });
    }
    const u = c.lost_unexplained ?? 0;
    if (u > 0) {
      out.push({ key: "unexplained", points: u, sign: "-", what: "Not itemised by the grader.",
                 more: "It gave this criterion that much less without saying where." });
    } else if (u < 0) {
      out.push({ key: "over", points: null, sign: "-",
                 what: "The grader's items add up to more than was taken off.",
                 more: "The mark is the console's; the items are shown as the grader gave them." });
    }
  }
  // 🔴 A REVIEW'S CHANGE IS A LINE TOO, so the card adds up to the mark a release would post.
  // ⚠️ The teacher's note stays on the card, beside the verdict; here the reason is enough.
  if (!advisory && v?.verdict === "adjust") {
    const d = v.points - (v.agent_points ?? c.earned ?? 0);
    if (d !== 0) {
      out.push({ key: "review", points: Math.abs(d), sign: d > 0 ? "+" : "-",
                 what: `Your review: ${reasonLabel(v.reason)}.` });
    }
  }
  return out;
}

/**
 * The points one criterion lost, each with what and where — inside that criterion's card.
 *
 * 🔴 ONE PLACE PER CRITERION (owner, 2026-10-01: "the criteria should be all together no split!
 * gets messy!"). The deductions sat in a panel of their own above the cards, so a criterion's
 * points were explained in one place and its reasoning, citations and verdict in another. They
 * are part of the criterion now, directly under its summary.
 * 🔴 EVERY NUMBER HERE IS THE SERVER'S. The grader itemised its own deductions (`lost`), and the
 * console recorded what it took off itself (`lost_console`) and what nobody itemised
 * (`lost_unexplained`). This prints them and adds nothing up.
 * ⚠️ AN ADVISORY CRITERION'S LOSSES ARE MARKED AS NOT COUNTED: what it WOULD have cost is the
 * reason for judging it, and reading it as part of the mark would be false.
 */
export function Deductions({ r, c, v }: {
  r: Report; c: Crit; v?: NonNullable<Report["review"]>["criteria"][number];
}) {
  // ⚠️ A HELD GRADE HAS NO MARK TO EXPLAIN: our failure, not the student's points.
  if (!r.gradeable || r.points == null) return null;
  const advisory = c.result === "advisory";
  const lines = whyLines(c, advisory ? undefined : v);
  if (advisory && c.lost === undefined) return null;
  if (lines.length === 0) return null;
  return (
    <div className={advisory ? "rp-taken rp-taken-adv" : "rp-taken"}>
      <div className="rp-taken-k">{advisory ? "Points it would have lost — not counted"
                                            : "Points taken off"}</div>
      <ul className="rp-list" aria-label={`Points taken off ${c.id}`}>
        {lines.map((l) => (
          <li className="rp-item" key={l.key}>
            <span className={l.sign === "+" ? "rp-pts rp-pts-plus" : "rp-pts"}>
              {l.points == null ? "" : l.sign === "+" ? `+${pts(l.points)}` : minus(l.points)}
            </span>
            <div>
              {/* ⚠️ INLINE MARKDOWN, SO `close` READS AS CODE AND NOT AS TWO BACKTICKS — and
                  without links, because the grader wrote it. */}
              <div className="rp-what"><MarkdownInline text={l.what} links={false} /></div>
              {l.more && <div className="rp-more"><Markdown text={l.more} links={false} /></div>}
              {(l.cites?.length ?? 0) > 0 && (
                <div className="rp-where">
                  {l.cites!.map((e, i) => {
                    const href = citeHref(r.repo, `${e.file}:${e.line}`);
                    const label = `${repoPath(e.file)}:${e.line}`;
                    return href
                      ? <a key={i} className="rp-cite" href={href} target="_blank"
                           rel="noreferrer">{label}</a>
                      : <span key={i} className="rp-cite">{label}</span>;
                  })}
                </div>)}
            </div>
          </li>))}
      </ul>
    </div>);
}

const VERDICT_WORD = { met: "met", partly: "partly", missing: "missing" } as const;

/** Every requirement of a criterion's tasks, those in subtasks included. */
function requirementsOf(c: Crit): RequirementResult[] {
  return (c.tasks ?? []).flatMap((t) => [
    ...t.requirements, ...(t.subtasks ?? []).flatMap((st) => st.requirements)]);
}

/**
 * Whether a criterion's tasks carry what each requirement cost, so that task by task IS its
 * account of the points (2026-10-13).
 *
 * 🔴 TASK BY TASK REPLACES "POINTS TAKEN OFF" (owner, 2026-10-13: "since is the same basically").
 * For a criterion scored from its tasks every lost item is one requirement, so the two said the
 * same thing twice; the cost now sits on the requirement's own line.
 * ⚠️ A GRADE MADE BEFORE THEN has the costs only as lost items, so its card keeps "Points taken
 * off" beside the tasks — showing it without would drop the one place its points are explained.
 * A grade that lost nothing has nothing to show either way.
 */
export function costed(c: Crit): boolean {
  const reqs = requirementsOf(c);
  return reqs.length > 0 && (reqs.some((q) => q.lost != null) || (c.lost ?? []).length === 0);
}

/**
 * A criterion scored from its tasks, task by task: what the grader answered for each requirement,
 * what the console counted, what it cost and what it cited (2026-10-07, costs 2026-10-13).
 *
 * 🔴 THE ANSWER AND WHAT WAS COUNTED ARE BOTH SHOWN WHEN THEY DIFFER. A requirement answered met
 * with nothing that checks out counts as missing, and a teacher reading only "missing" would think
 * the grader said so — so the card prints the server's `fault`, which names the answer and why it
 * was counted otherwise.
 * ⚠️ EVERY NUMBER IS THE SERVER'S: a task's points and what it earned, and a requirement's cost,
 * come from `validate_agent`. The lines at the foot are the ones no requirement holds — extra
 * credit, the console's own rounding, your review's change — so the card still adds up to the mark.
 * ⚠️ A HELD GRADE SHOWS NO COST: there is no mark, and our failure is not the student's points.
 */
export function TaskResults({ r, c, v }: {
  r: Report; c: Crit; v?: NonNullable<Report["review"]>["criteria"][number];
}) {
  if (!c.tasks?.length) return null;
  const advisory = c.result === "advisory";
  const withCost = costed(c) && r.gradeable && r.points != null;
  const foot = withCost
    ? whyLines(c, advisory ? undefined : v).filter((l) => !l.key.startsWith("lost-")) : [];
  const cost = (q: RequirementResult, counts: string) => {
    if (!withCost) return null;
    if (counts !== "required") {
      return <span className="rp-cost rp-cost-none">
        {counts === "extra_credit" ? "extra credit" : "counts nothing"}</span>;
    }
    return q.lost
      ? <span className="rp-cost">{minus(q.lost)}</span>
      : <span className="rp-cost rp-cost-none" aria-label="nothing lost">{DASH}</span>;
  };
  const reqList = (reqs: RequirementResult[], counts: string) => (
    <ul className="rp-reqs">
      {reqs.map((q) => (
        <li key={q.id} className={withCost ? "rp-req rp-req-cost" : "rp-req"}>
          <span className={`rp-v ${q.counted}`}>{VERDICT_WORD[q.counted]}</span>
          <div>
            <div className="rp-req-t"><span className="rp-req-id">{q.id}</span> {q.text}</div>
            {/* The server's sentence already says what was answered and why it was counted
                otherwise; prefixing the answer again said it twice. */}
            {q.fault && <div className="rp-req-more">{q.fault}</div>}
            {q.detail && (
              <div className="rp-req-more"><Markdown text={q.detail} links={false} /></div>)}
            {q.evidence.length > 0 && (
              <div className="rp-where">
                {q.evidence.map((e, i) => {
                  const href = citeHref(r.repo, `${e.file}:${e.line}`);
                  const label = `${repoPath(e.file)}:${e.line}`;
                  return href
                    ? <a key={i} className="rp-cite" href={href} target="_blank"
                         rel="noreferrer">{label}</a>
                    : <span key={i} className="rp-cite">{label}</span>;
                })}
              </div>)}
          </div>
          {cost(q, counts)}
        </li>))}
    </ul>);
  /** A unit's mark, what it lost, and a bar — the gap is printed only where points can be lost. */
  const head = (id: string, title: string, earned: number | null, points: number,
                counts: string, badge?: ReactNode) => {
    const gap = withCost && counts === "required" && earned != null ? points - earned : 0;
    return (<>
      <div className="rp-task-h">
        <code className="tk-id">{id}</code>
        <span className="rp-task-t">{title}</span>
        {badge}
        {gap > 0 && <span className="rp-tlost">{minus(gap)}</span>}
        <span className="rp-task-p">
          {earned == null ? "reported" : <>{pts(earned)}<small>/{points}</small></>}</span>
      </div>
      {withCost && earned != null && points > 0 && (
        <span className="rp-tbar" aria-hidden="true">
          <i style={{ width: `${Math.max(0, Math.min(1, earned / points)) * 100}%` }} /></span>)}
    </>);
  };
  return (
    <div className={advisory ? "rp-tasks rp-tasks-adv" : "rp-tasks"}>
      <div className="rp-taken-k">
        {advisory && withCost ? "Task by task — what it would have lost, not counted"
                              : "Task by task"}</div>
      {c.task_summary && <p className="rp-task-sum">{c.task_summary}</p>}
      <ol className="rp-tlist" aria-label={`Tasks of ${c.id}`}>
        {c.tasks.map((t) => (
          <li key={t.id} className="rp-task">
            {head(t.id, t.title, t.earned, t.points, t.counts,
                  t.counts !== "required"
                    ? <Badge tone="plain">{t.counts === "extra_credit" ? "extra credit"
                                                                       : "optional"}</Badge>
                    : undefined)}
            {t.description && <p className="rp-task-d">{t.description}</p>}
            {(t.subtasks ?? []).length > 0 ? (
              <ol className="rp-subs" aria-label={`Subtasks of ${t.id}`}>
                {(t.subtasks ?? []).map((st) => (
                  <li key={st.id} className="rp-sub">
                    {head(st.id, st.title, st.earned, st.points, t.counts)}
                    {st.description && <p className="rp-task-d">{st.description}</p>}
                    {reqList(st.requirements, t.counts)}
                  </li>))}
              </ol>
            ) : reqList(t.requirements, t.counts)}
          </li>))}
      </ol>
      {foot.length > 0 && (
        <ul className="rp-list rp-tfoot" aria-label={`Also on ${c.id}`}>
          {foot.map((l) => (
            <li className="rp-item" key={l.key}>
              <span className={l.sign === "+" ? "rp-pts rp-pts-plus" : "rp-pts"}>
                {l.points == null ? "" : l.sign === "+" ? `+${pts(l.points)}` : minus(l.points)}
              </span>
              <div>
                <div className="rp-what"><MarkdownInline text={l.what} links={false} /></div>
                {l.more && <div className="rp-more"><Markdown text={l.more} links={false} /></div>}
              </div>
            </li>))}
        </ul>)}
    </div>);
}

/** Where a teacher stands on one criterion, as the panel shows it. */
type Stance = { key: "undecided" | "agreed" | "adjusted" | "advisory"; points?: string };

/** The report's four tabs (owner, 2026-10-13: "have an overview tab the first one!"). */
export type Pane = "overview" | "criteria" | "grader" | "provenance";

/** Which tab holds a part of the report an address names: `#crit-C2` is a criterion, the rest
 *  are its sections. ⚠️ Every id here is one the report draws; an address naming nothing lands on
 *  the Overview, where the report opens anyway. */
const PANE_OF: Record<string, Pane> = {
  "rp-flags": "overview", "rp-problems": "overview", "rp-student": "overview",
  "rp-teacher": "overview", "rp-release": "overview", tests: "criteria",
  "rp-grader": "grader", "rp-log": "grader", "rp-changes": "grader",
  "rp-provenance": "provenance", "rp-classroom": "provenance",
};

export function targetOf(id: string, per: { id: string }[]): { pane: Pane; crit?: string } | null {
  if (id.startsWith("crit-")) {
    const cid = id.slice(5);
    return per.some((c) => c.id === cid) ? { pane: "criteria", crit: cid } : null;
  }
  const p = PANE_OF[id];
  return p ? { pane: p } : null;
}

/** Left and right move along a tab strip and take the focus with them — the tablist pattern. */
function arrows<T extends string>(ids: T[], at: T, to: (t: T) => void, prefix: string) {
  return (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const i = Math.max(0, ids.indexOf(at));
    const n = ids[(i + (e.key === "ArrowRight" ? 1 : ids.length - 1)) % ids.length]!;
    to(n);
    document.getElementById(`${prefix}${n}`)?.focus();
  };
}

/** A criterion's score as its tab says it: an advisory one is never read as a student's 0. */
function tabScore(c: Crit): string {
  if (c.result !== "advisory") return `${c.earned ?? DASH}/${c.weight}`;
  const w = wouldBe(c);
  return w == null ? "advisory" : `${w}/${c.advisory_weight ?? 0} · advisory`;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** The figure an advisory criterion would have been, or null when nobody judged it against a
 *  weight.
 * ⚠️ AN ADVISORY FIGURE FROM BEFORE 2026-09-29 THAT READS 0 IS NOT A JUDGEMENT. The grader was
 * sent the criterion with a weight of 0 and no word on `advisory_weight`, so it judged it out of
 * 0; a 0 then says nothing, where a positive figure shows it judged against the weight. */
function wouldBe(c: Crit): number | null {
  return c.result === "advisory" && c.advisory_points != null
    && (c.lost !== undefined || c.advisory_points > 0) ? c.advisory_points : null;
}

/** What "escalated" means, in full.
 *
 * 🔴 BEHIND THE BADGE, NEVER PROSE ON THE PAGE (owner, 2026-10-03). This was a red alert across
 * the top of the report, in front of the criteria a teacher came to read. What a reader must not
 * miss is the STATE — the badge beside the mark, and one line under it saying what has to
 * happen; the why is in the `Why` panel beside the badge, which opens on hover, focus and tap.
 * ⚠️ NOT A `title=`: that reached a mouse and nothing else (review, 2026-10-03). */
export function escalationWhy(mayReview: boolean): string {
  return "This grade is escalated: it is released only once somebody has reviewed it. The "
    + "grader could not stand behind the number — an unevidenced judgement, an abstention, an "
    + "injection attempt, or a build that failed for reasons that are not the student's. A "
    + "rubric with no reference suite always escalates: with no tests there is nothing to "
    + "recompute, so the agent's own numbers would pass straight through. "
    + (mayReview ? "Agree or Adjust on every criterion; your total is what a release posts."
                 : "Somebody with release rights on this cohort has to review it.");
}

/**
 * The criteria, what the agent said about each, and — for somebody who may release — the
 * teacher's verdict on each and the review that records them.
 *
 * 🔴 THE CARDS ARE READ ON THE LEFT; THE GRADE IS DECIDED IN ONE PANEL ON THE RIGHT (owner,
 * 2026-10-04, option 2 of the mockups: https://claude.ai/artifact/R71LXxJorxH5D4u3rCsnca). The
 * panel holds, top to bottom: the result, every criterion with its own Agree or Adjust directly
 * under it, and the review — the total, the whole-grade note and Save. A criterion is listed
 * once: its row in the panel IS where its verdict is given, so nothing is written twice.
 * ⚠️ "On this page" went with it ("remove what is on this page!").
 * 🔴 EVERY CRITERION'S CARD IS ON THE PAGE AT ONCE, each at `#crit-<id>` — a native anchor, so a
 * row in the panel and TeacherTests' "Counted under C1" are plain links the browser follows, and
 * a Ctrl- or Cmd-click opens one in a new tab with nothing to cancel.
 * 🔴 THE DRAFT LIVES HERE, ABOVE BOTH, so a look at By file and back loses no verdict, and the
 * running total is the sum of every row's.
 * 🔴 THE PAGE IS IN ITS DRAWN ORDER AT EVERY WIDTH (WCAG 2.4.3): the cards, then the panel, then
 * the rest. Two columns place the panel beside both; one column draws it after the cards, so on a
 * phone the verdicts come straight after what they are about, never under Provenance.
 * ⚠️ IT WRITES NOTHING TO CLASSROOM. A saved review decides what a release posts; the release
 * below is still the separate, previewed step that reaches the student.
 */
/** Where a review stands, for whoever holds the report: a sheet asks before discarding a draft,
 *  and its phone dock says how far it has got. */
export interface DraftState { editing: boolean; dirty: boolean; decided: number; of: number }

export function Criteria({ r, courseId, onSaved, onDraft, panes, pane, crit, go, select,
                          pick }: {
  r: Report; courseId: string; onSaved: () => void;
  onDraft?: (d: DraftState) => void;
  /** What the other three tabs hold; the report builds them, this lays them out. */
  panes: { overview: ReactNode; grader: ReactNode; provenance: ReactNode };
  /** The tab and the criterion shown. They live in the report, so a review saved — which
   *  remounts this — leaves the reader where they were. */
  pane: Pane; crit: string;
  /** Show the part of the report an id names, and go there. */
  go: (id: string) => void;
  select: (p: Pane) => void;
  pick: (cid: string) => void;
}) {
  const reviewable = r.gradeable && !r.released && r.may_review;
  const [editing, setEditing] = useState(reviewable && !r.review);
  const [draft, setDraft] = useState(() => startingDraft(r));
  const [note, setNote] = useState(r.review?.note ?? "");
  const [calibration, setCalibration] = useState(r.review?.calibration ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const showVerdicts = editing || r.review != null;
  const set = (id: string, patch: Partial<Verdict>) =>
    setDraft((d) => ({ ...d, [id]: { ...d[id]!, ...patch } }));

  // 🔴 AN ADVISORY CRITERION IS NOT REVIEWABLE, and the server refuses a verdict on one by
  // name. It carries no weight by the teacher's own choice, so Agree has no mark to match and
  // the only legal adjustment is the 0 it already has — every route out is closed. It stays on
  // the page with its reasoning and citations; what it is not is part of a number a teacher
  // signs their name to.
  const reviewed = r.per_criterion.filter((c) => c.result !== "advisory");
  const parts = reviewed.map((c) => scored(c, draft[c.id]!));
  const decided = parts.filter((x) => x != null).length;
  const total = decided === parts.length
    ? Math.round(parts.reduce<number>((a, x) => a + (x ?? 0), 0) * 100) / 100 : null;
  const held = new Map((r.review?.criteria ?? []).map((v) => [v.id, v]));
  const per = r.per_criterion;
  const shown = per.some((c) => c.id === crit) ? crit : (per[0]?.id ?? "");

  // 🔴 DIRTY MEANS SOMETHING WOULD BE LOST: a verdict, a note or the calibration tick moved from
  // where this review started. Opening the form and changing nothing is not a draft.
  const dirty = editing && (JSON.stringify(draft) !== JSON.stringify(startingDraft(r))
    || note !== (r.review?.note ?? "") || calibration !== (r.review?.calibration ?? false));
  useEffect(() => {
    onDraft?.({ editing, dirty, decided, of: parts.length });
  }, [onDraft, editing, dirty, decided, parts.length]);

  // 🔴 A LINK TO A PART OF THE REPORT OPENS ITS TAB FIRST (2026-10-13). Every criterion and every
  // section is in a tab, and a browser does not scroll to a hidden element — so a plain click on
  // `#crit-C1` (a row in the panel, "Counted under C1") shows the Criteria tab at C1, and the
  // report then goes there. ⚠️ It does not cancel the click: the address still becomes `#crit-C1`.
  // ⚠️ A modified or middle click is the browser's, and opens a new tab instead.
  const follow = (e: MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element
      ? e.target.closest<HTMLAnchorElement>('a[href^="#"]') : null;
    if (!a) return;
    const id = safeDecode((a.getAttribute("href") ?? "").slice(1));
    if (targetOf(id, per)) go(id);
  };

  // ⚠️ ADJUST OPENS ITS FIELDS UNDER THE ROW, IN A PANEL THAT SCROLLS UNDER A PINNED FOOT, so they
  // can open out of sight — the points box behind Your review. Brought into view, never focused:
  // a radio a keyboard is arrowing through keeps its focus.
  const reveal = useRef<string | null>(null);
  useLayoutEffect(() => {
    const id = reveal.current;
    if (id === null) return;
    reveal.current = null;
    document.getElementById(`rp-adj-${id}`)?.scrollIntoView?.({ block: "nearest" });
  }, [draft]);

  const save = () => {
    // ⚠️ AN UNDECIDED CRITERION IS LEFT OUT, NOT SENT BLANK: the server then names it —
    // "the review leaves out C2" — where a blank verdict would be refused as a malformed body.
    const criteria = reviewed.flatMap((c): ReviewCriterionInput[] => {
      const v = draft[c.id]!;
      if (v.verdict === "agree") return [{ id: c.id, verdict: "agree" }];
      if (v.verdict === "adjust") {
        return [{ id: c.id, verdict: "adjust", reason: v.reason, note: v.note,
                  points: v.points.trim() === "" ? null : Number(v.points) }];
      }
      return [];
    });
    setBusy(true); setErr(null);
    tracked(reviewGrade(courseId, r.submission_id, {
      graded_at: r.graded_at ?? "", criteria, note, calibration, confirm: true }))
      .then(() => onSaved())
      .catch((e) => setErr(e as ApiError))
      .finally(() => setBusy(false));
  };

  /** Where the teacher stands on one criterion: the draft while reviewing, else the review in
   *  force. Nothing at all for somebody who sees no verdicts — except advisory, which is a fact
   *  about the criterion rather than about anybody's review. */
  const stanceOf = (c: Crit): Stance | null => {
    if (c.result === "advisory") return { key: "advisory" };
    if (!showVerdicts) return null;
    if (editing) {
      const v = draft[c.id]!;
      if (v.verdict === "agree") return { key: "agreed" };
      if (v.verdict === "adjust") return { key: "adjusted", points: v.points.trim() };
      return { key: "undecided" };
    }
    const s = held.get(c.id);
    if (s?.verdict === "agree") return { key: "agreed" };
    if (s?.verdict === "adjust") return { key: "adjusted", points: String(s.points) };
    return { key: "undecided" };
  };

  /** One criterion's verdict, under its row in the panel. ⚠️ No "Your verdict" label of its own:
   *  the panel's heading says it once, and each radiogroup carries the criterion in its name. */
  const verdict = (c: Crit) => {
    const judged = c.result === "judged";
    const v = draft[c.id]!;
    const stored = held.get(c.id);
    if (!showVerdicts) return null;
    if (c.result === "advisory") {
      return (
        <p className="rp-sv rp-sv-adv">Advisory on this assignment: judged and reported, counting
          nothing, so there is no mark of it to agree with.</p>);
    }
    if (editing) {
      return (
        <div className="rp-sv">
          <div className="verdict-choices" role="radiogroup" aria-label={`Your verdict on ${c.id}`}>
            <label className="verdict-choice">
              <input type="radio" name={`verdict-${c.id}`} value="agree"
                     checked={v.verdict === "agree"} disabled={!judged}
                     onChange={() => set(c.id, { verdict: "agree" })} />
              Agree{judged && <span className="muted num"> · {c.earned}</span>}
            </label>
            <label className="verdict-choice">
              <input type="radio" name={`verdict-${c.id}`} value="adjust"
                     checked={v.verdict === "adjust"}
                     onChange={() => { reveal.current = c.id; set(c.id, { verdict: "adjust" }); }} />
              Adjust
            </label>
          </div>
          {/* The reason Agree is unavailable, where it is unavailable — a greyed choice with no
              reason reads as broken. */}
          {!judged && (
            <p className="small muted">The agent never judged this, so it gave no mark to agree
              with. Adjust it — to 0 if 0 is right.</p>)}
          {v.verdict === "adjust" && (
            <div className="verdict-adjust" id={`rp-adj-${c.id}`}>
              <span className="verdict-pts">
                <input className="input num" inputMode="decimal" value={v.points}
                       aria-label={`Your points for ${c.id}, out of ${c.weight}`}
                       onChange={(e) => set(c.id, { points: e.target.value })} />
                <span className="muted small">/ {c.weight}</span>
              </span>
              <select className="select" value={v.reason} aria-label={`Why you moved ${c.id}`}
                      onChange={(e) => set(c.id, { reason: e.target.value })}>
                <option value="">Why did you move it?</option>
                {r.review_reasons.map((x) => (
                  // ⚠️ `other` NEEDS THE NOTE, which the server refuses without — so the
                  // picker says so where the choice is made.
                  <option key={x} value={x}>
                    {reasonLabel(x)}{x === "other" ? " — say in the note" : ""}</option>))}
              </select>
              <textarea className="ta short" rows={2} value={v.note} aria-label={`Note on ${c.id}`}
                        placeholder={v.reason === "other" ? "Say what the reason is"
                                                          : "Note (optional)"}
                        onChange={(e) => set(c.id, { note: e.target.value })} />
            </div>)}
        </div>);
    }
    // Read only: the review in force, where the controls would be. The stance is a word and a
    // mark, as everywhere else — these get screenshotted into chat, where colour does not survive.
    const st = stanceOf(c);
    return (
      <div className="rp-sv rp-sv-read">
        <div className="row small">
          {st && st.key !== "undecided" && (
            <span className={`rp-vs rp-vs-${st.key}`} data-stance={st.key}>
              {st.key}{st.points ? ` · ${st.points}` : ""}</span>)}
          {stored?.verdict === "adjust" && (
            <span className="num">{stored.agent_points ?? DASH} → {stored.points}</span>)}
          {!stored && <span className="muted">No verdict on this criterion.</span>}
          {stored?.reason && <span className="muted">{reasonLabel(stored.reason)}</span>}
        </div>
        {stored?.note && <p className="rp-sv-note">{stored.note}</p>}
      </div>);
  };

  const card = (c: Crit, i: number) => {
    const advisory = c.result === "advisory";
    const mine = filesOf(r, c.id);
    const lines = mine.reduce((a, f) => a + f.lines.length, 0);
    const gap = advisory ? null : c.weight - (c.earned ?? 0);
    const would = wouldBe(c);
    return (
      <article key={c.id} className="card rp-panel" id={`crit-${c.id}`} data-crit={c.id}
               aria-labelledby={`rp-h-${c.id}`}>
        <header className="rp-ph">
          <span className="eyebrow">Criterion {i + 1} of {per.length}</span>
          <div className="rp-ph-row">
            <h3 className="rp-ph-title" id={`rp-h-${c.id}`}>
              {/* ⚠️ `{" "}`: JSX drops the line break between two elements, and the heading's
                  name would read "C1Does what the assignment asked". */}
              <span className="rp-id">{c.id}</span>{c.label ? <>{" "}{c.label}</> : null}
            </h3>
            <div className="rp-card-score">
              {/* 🔴 AN ADVISORY CRITERION SHOWS WHAT IT WOULD HAVE BEEN, AND SAYS IT DOES NOT
                  COUNT. "0/0" would be arithmetically true and read as a student earning
                  nothing. */}
              {advisory ? (<>
                <span className="rp-n rp-n-would">
                  {would ?? DASH}{would != null && <span className="rp-d">/{c.advisory_weight ?? 0}</span>}
                </span>
                <span className="rp-note">
                  {would != null ? "would be · " : "not judged against a weight · "}
                  <span>does not count</span></span>
              </>) : (<>
                <span className="rp-n">{c.earned ?? DASH}<span className="rp-d">/{c.weight}</span></span>
                {gap != null && gap > 0 && <span className="rp-lost">{minus(gap)}</span>}
              </>)}
            </div>
          </div>
          <div className="rp-card-meta">
            {/* 🔴 `result`, NOT `evidence`: whether the agent reached a verdict at all —
                `judged`, or `absent` when it never judged the criterion (scored 0 and
                escalated). ⚠️ `plain` for advisory: a warning tone would read as something
                having gone wrong with a grade that is working as configured. */}
            <Badge tone={advisory ? "plain" : c.result === "judged" ? "ok" : "warn"}>
              {c.result ?? "?"}</Badge>
            {c.confidence != null && <span>confidence {c.confidence}</span>}
            {lines > 0 && <span>{lines} citation{lines === 1 ? "" : "s"}</span>}
          </div>
        </header>
        <div className="rp-pbody">
          {c.summary && (
            <div className="rp-sum"><Markdown text={c.summary} links={false} /></div>)}
          {!c.summary && !c.reasoning && c.result === "absent" && (
            <p className="small muted">The grader never judged this criterion, so it scored 0.</p>)}
          {/* 🔴 TASK BY TASK IS THE ACCOUNT OF A CRITERION SCORED FROM ITS TASKS (2026-10-13), each
              requirement with what it cost. A criterion with no tasks — and a task grade made
              before the costs were stored — keeps its list of points taken off. */}
          {!costed(c) && <Deductions r={r} c={c} v={held.get(c.id)} />}
          <TaskResults r={r} c={c} v={held.get(c.id)} />
          {/* 🔴 A CRITERION'S TESTS ARE IN ITS CARD (owner, 2026-10-05): a test counts under one
              criterion and a failure is charged there, so it is read beside the points it cost. */}
          <CriterionTests r={r} crit={c.id} />
          {/* 🔴 THE REASONING IS CLOSED UNTIL OPENED (owner, 2026-10-13): the summary and the
              tasks say what was decided, the reasoning says why at length. ⚠️ Open on a grade
              made before summaries existed, whose card would otherwise say nothing at all. */}
          {c.reasoning && (
            <details className="rp-disc" open={!c.summary}>
              <summary>Reasoning</summary>
              <div className="rp-disc-body rp-read">
                <Markdown text={c.reasoning} links={false} /></div>
            </details>)}
          {/* What it cited, file by file — which is where By file went (owner, 2026-10-13). */}
          {mine.length > 0 && (
            <details className="rp-disc">
              <summary>What it cited <span className="rp-cnt">
                · {lines} line{lines === 1 ? "" : "s"} in {mine.length} file
                {mine.length === 1 ? "" : "s"}</span></summary>
              <div className="rp-disc-body rp-cites">
                {mine.map((f) => (
                  <div key={f.file}>
                    <div className="rp-path"><CitePath r={r} file={f.file} /></div>
                    {/* The grader's note on a file it cites — By file was its only reader. */}
                    {f.note && (
                      <div className="rp-fnote">
                        <span className="rp-by">Grader's note</span>
                        <span className="rp-fnote-text"><Markdown text={f.note} links={false} /></span>
                      </div>)}
                    <div className="rp-lines">
                      {f.lines.map((l) => (
                        <CiteLine key={l.line} r={r} file={f.file} line={l.line} quote={l.quote}
                                  caption={forWhat(l, false)}
                                  tone={l.lost ? (l.counted ? "lost" : "would") : undefined} />))}
                    </div>
                  </div>))}
              </div>
            </details>)}
        </div>
      </article>);
  };

  /** One criterion in the panel: a plain link to its card, and under it the verdict on it.
   *  ⚠️ No selection state — the row summarises; it does not choose what the page shows.
   *  ⚠️ The verdict sits beside the link, never inside it: a control inside an anchor is a control
   *  nobody can operate without following the link. */
  const sheetRow = (c: Crit) => {
    const advisory = c.result === "advisory";
    const shown = advisory ? wouldBe(c) : c.earned;
    const of = advisory ? (c.advisory_weight ?? 0) : c.weight;
    const frac = shown != null && of > 0 ? Math.max(0, Math.min(1, shown / of)) : 0;
    // ⚠️ ONLY ADVISORY WEARS A STANCE ON ITS ROW: it is a fact about the criterion. Where a
    // teacher stands on the others is the verdict right under the row — said there, once.
    const st = advisory ? stanceOf(c) : null;
    // 🔴 AN ADVISORY CRITERION NOBODY JUDGED AGAINST A WEIGHT HAS NO "/30", here as on its card:
    // a denominator says it was judged out of 30, which is the one thing nobody did.
    const unweighed = advisory && shown == null;
    // ⚠️ NAMED IN WORDS: the row is five spans laid out by a grid, and its content read as a
    // name runs them together — "C1Does the thing54/70agreed".
    const name = [`${c.id}${c.label ? ` ${c.label}` : ""}`,
                  unweighed ? "not judged against a weight, does not count"
                    : `${shown ?? "no mark"} of ${of}${advisory ? ", would be" : ""}`,
                  st ? `${st.key}${st.points ? ` ${st.points}` : ""}` : ""]
      .filter(Boolean).join(", ");
    return (
      <li key={c.id}>
        <a href={`#crit-${c.id}`} aria-label={name} className="rp-rrow">
          <span className="rp-id">{c.id}</span>
          <span className="rp-rrow-label">{c.label ?? ""}</span>
          <span className={advisory ? "rp-rrow-n rp-rrow-would" : "rp-rrow-n"}>
            {shown ?? DASH}{!unweighed && <span className="rp-rrow-of">/{of}</span>}</span>
          <span className={advisory ? "rp-bar rp-bar-would" : "rp-bar"} aria-hidden="true">
            <i style={{ width: `${frac * 100}%` }} /></span>
          {st && (
            <span className={`rp-vs rp-vs-${st.key}`} data-stance={st.key}>
              {st.key}{st.points ? ` · ${st.points}` : ""}</span>)}
        </a>
        {verdict(c)}
      </li>);
  };

  const headline = r.review?.points ?? r.points;
  // 🔴 THE STATE A NUMBER MUST NOT LEAVE, AS A BADGE BESIDE THE ONE IT QUALIFIES. Only while it
  // holds: a review in force is what lets an escalated grade out, and the rail then says who
  // reviewed it and what the agent gave.
  const escalated = r.escalate && !r.review && r.gradeable && !r.released;
  const why = escalationWhy(r.may_review);
  // 🔴 THE REVIEW IS WHERE YOU FINISH, AND IT STAYS IN SIGHT: the panel's foot, pinned under the
  // criteria while they scroll — the total a release would post, how much is decided, the
  // whole-grade note and Save.
  const review = editing ? (
    <section className="rp-sheet-foot rp-review" id="rp-review" aria-labelledby="rp-review-h">
      <div className="rp-review-sum">
        <h3 className="rp-sheet-k" id="rp-review-h">Your review</h3>
        <div className="rp-total">
          <span className="num rp-total-n">{total ?? DASH}</span>
          <span className="rp-total-of">/{r.max_points} your total</span>
        </div>
        <div className="rp-review-meta">
          <span>{decided} of {parts.length} decided</span>{" · "}
          <span>the agent gave {r.points ?? DASH}</span>
        </div>
        <div className="rp-steps" aria-hidden="true">
          {reviewed.map((c, i) => (
            <i key={c.id} className={parts[i] != null ? "rp-step-on" : undefined} />))}
        </div>
      </div>
      <div className="rp-review-form">
        <textarea className="ta prose rp-review-note" rows={2} value={note}
                  aria-label="A note on the whole grade"
                  placeholder="A note on the whole grade (optional)"
                  onChange={(e) => setNote(e.target.value)} />
        <label className="rp-calib">
          <input type="checkbox" checked={calibration}
                 onChange={(e) => setCalibration(e.target.checked)} />
          <span>Keep this as a calibration case — a grade you are sure of, that later
            changes to the grader are measured against</span>
        </label>
        {err && <ErrorBox err={err} />}
        <div className="rp-review-acts">
          {/* ⚠️ DIMMED, NEVER DISABLED, while a criterion is undecided: the server's
              refusal is what names the one that is missing. */}
          <button className={decided === parts.length ? "btn primary" : "btn primary dim"}
                  disabled={busy} onClick={save}>
            {busy ? "Saving…" : r.review ? "Save this review instead" : "Save review"}</button>
          {r.review && (
            <button className="btn" disabled={busy}
                    onClick={() => {
                      // ⚠️ EVERYTHING goes back to the review in force — a note edited and
                      // then abandoned must not reappear the next time somebody reviews.
                      setEditing(false); setErr(null); setDraft(startingDraft(r));
                      setNote(r.review?.note ?? "");
                      setCalibration(r.review?.calibration ?? false);
                    }}>Cancel</button>)}
          <span className="rp-review-fine">Writes nothing to Classroom. A release then posts
            your total.</span>
        </div>
      </div>
    </section>) : null;

  const alerts = r.flags.length + r.problems.length;
  const tabs: { id: Pane; label: string; count?: ReactNode }[] = [
    { id: "overview", label: "Overview",
      count: alerts > 0 ? <span className="count rp-count-warn">{alerts}</span> : null },
    { id: "criteria", label: "Criteria", count: <span className="count">{per.length}</span> },
    { id: "grader", label: "What the grader did" },
    { id: "provenance", label: "Provenance" },
  ];
  // 🔴 ONE TAB PER CRITERION (owner, 2026-10-13: "each criteria is a tab inside criteria!").
  // ⚠️ HIDDEN, NOT UNMOUNTED: a card's open sections survive a look at another criterion.
  // ⚠️ With one criterion there is nothing to choose, so there is no strip.
  const many = per.length > 1;
  const criteria = (<>
    {per.length === 0 && <p className="small muted">No criterion was recorded for this grade.</p>}
    {many && (
      <div className="rp-modes rp-ctabs" role="tablist" aria-label="Criteria"
           onKeyDown={arrows(per.map((c) => c.id), shown, pick, "rp-ctab-")}>
        {per.map((c) => (
          <button key={c.id} type="button" role="tab" className="tab" id={`rp-ctab-${c.id}`}
                  aria-controls={`rp-cpane-${c.id}`} aria-selected={shown === c.id}
                  tabIndex={shown === c.id ? 0 : -1} onClick={() => pick(c.id)}>
            <span className="rp-id">{c.id}</span>{c.label ? <>{" "}{c.label}</> : null}
            <span className={c.result === "advisory" ? "rp-ct rp-ct-adv" : "rp-ct"}>
              {tabScore(c)}</span>
          </button>))}
      </div>)}
    {per.map((c, i) => (
      <div key={c.id} className="rp-cpane" id={`rp-cpane-${c.id}`}
           role={many ? "tabpanel" : undefined}
           aria-labelledby={many ? `rp-ctab-${c.id}` : undefined}
           hidden={many && shown !== c.id}>{card(c, i)}</div>))}
    <TeacherTestsReport r={r} />
  </>);

  return (
    <div className="rp-grid" onClickCapture={follow}>
      <div className="rp-main">
        <div className="tabs rp-tabs" role="tablist" aria-label="Grade report"
             onKeyDown={arrows(tabs.map((t) => t.id), pane, select, "rp-tab-")}>
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" className="tab" id={`rp-tab-${t.id}`}
                    aria-controls={`rp-pane-${t.id}`} aria-selected={pane === t.id}
                    tabIndex={pane === t.id ? 0 : -1} onClick={() => select(t.id)}>
              {t.label}{t.count}</button>))}
        </div>
        {/* ⚠️ HIDDEN, NOT UNMOUNTED: what is open in one tab is still open on the way back. */}
        {tabs.map((t) => (
          <div key={t.id} className="rp-pane" id={`rp-pane-${t.id}`} role="tabpanel"
               aria-labelledby={`rp-tab-${t.id}`} hidden={pane !== t.id}>
            {t.id === "criteria" ? criteria : panes[t.id]}
          </div>))}
      </div>

      <div className="rp-side">
        <aside className="card rp-sheet" aria-label="This grade">
          <div className="rp-sheet-scroll">
            <section className="rp-sheet-sec rp-result" aria-labelledby="rp-result-h">
              <h3 className="rp-sheet-k" id="rp-result-h">Result</h3>
              {/* 🔴 THE MARK THAT GOES OUT, LARGE. With a review in force that is the teacher's
                  total, and the agent's number stays beside it rather than being the headline. */}
              <span className="num rp-mark">
                {headline == null ? DASH : headline}<span className="rp-mark-of">/{r.max_points}</span>
              </span>
              <span className="rp-states">
                <State s={r.status} />
                {escalated && (
                  <span className="rp-esc">
                    <span className="badge warn">escalated</span>
                    <Why label="Why this grade is escalated">{why}</Why>
                  </span>)}
              </span>
              {escalated && (
                <p className="rp-rail-why">{r.may_review
                  ? "Needs a review before it can be released"
                  : "Needs a review by somebody with Release"}</p>)}
              {r.review && (
                <span className="rp-rail-sub">reviewed · the agent gave {r.points ?? DASH}</span>)}
              <Meter per={per} />
            </section>
            <section className="rp-sheet-sec" aria-labelledby="rp-crit-k">
              <h3 className="rp-sheet-k" id="rp-crit-k">
                {showVerdicts ? "Criteria · your verdict" : "Criteria"}</h3>
              <ol className="rp-rail-list" aria-label="Criteria">{per.map(sheetRow)}</ol>
            </section>
            {r.review && !editing && (
              <section className="rp-sheet-sec" aria-labelledby="rp-reviewed-h">
                <h3 className="rp-sheet-k" id="rp-reviewed-h">Review</h3>
                <span className="rp-rail-sub">
                  by {r.review.reviewer} <When iso={r.review.created_at} />
                  {r.review.calibration && <> · <Badge tone="plain">calibration case</Badge></>}
                </span>
                {r.review.note && (
                  <p className="rp-rail-note"><span className="muted">Review note:</span>{" "}
                    {r.review.note}</p>)}
                {reviewable && (
                  <button className="btn sm rp-again" onClick={() => setEditing(true)}>
                    Review again</button>)}
              </section>)}
          </div>
          {review}
        </aside>
      </div>

    </div>
  );
}
/**
 * One grade, in full, at its own URL.
 *
 * 🔴 IT USED TO HAVE NO ADDRESS. The report was expanded inline under two different tables,
 * so the one document a teacher would want to send to a colleague — or come back to after
 * reading the code — could not be linked, and the back button did nothing. Now every row, every
 * log entry and every alarm that is about one grade points here.
 */
const FLAG_LABEL: Record<string, string> = {
  originality: "originality", misconduct: "misconduct", injection: "injection attempt",
  version_skew: "version skew", security: "security", grader_artefact: "grader artefact",
  profile_mismatch: "wrong language?",
};
const flagLabel = (k: string) => FLAG_LABEL[k] ?? k;

/**
 * What one grading used, in Provenance where the Cost row was: its tokens, its time, and how
 * far it moved the subscription's own figures.
 *
 * ⚠️ THE MOVEMENT IS AN UPPER BOUND and the line says so in words — your own Claude use in the
 * same minutes moves the same figure.
 * ⚠️ A GRADE FROM BEFORE THE UPDATE SAYS SO rather than showing a blank or a 0%: it keeps its
 * tokens and time, which every grade has always recorded.
 * 🔴 A GATEWAY RUN ALONE KEEPS A DOLLAR FIGURE, because there it is real — measured at the
 * gateway, paid per token. It draws nothing from the subscription and says that instead.
 */
function UsageRows({ r }: { r: Report }) {
  const u = r.usage;
  const total = tokenTotal(u.tokens);
  const turns = u.num_turns ?? r.num_turns;
  const w7 = moveOf(u.subscription, "seven_day"), w5 = moveOf(u.subscription, "five_hour");
  const weekly = moveRange(w7), five = moveRange(w5);
  // ⚠️ A WINDOW THAT RESET WHILE THE RUN WENT BOUNDS NOTHING, so "at most what the run used" is
  // said only of a window that moved or was read once.
  const bounded = [w7, w5].some((w) => w != null && !resetDuring(w));
  // 🔴 THE SERVER'S OWN BOOLEAN. `r.grader.route` is `litellm` for a gateway run — never the
  // word `gateway` — and comparing with that word matched no run there has ever been.
  const gateway = u.gateway;
  return (<>
    <dt>Tokens</dt>
    <dd>{u.tokens
      ? <><b className="num">{runTokens(total)}</b> — <span className="num">{tokenCount(
            u.tokens.input)}</span> input · <span className="num">{tokenCount(
            u.tokens.output)}</span> output · <span className="num">{tokenCount(
            u.tokens.cache_read)}</span> read from cache · <span className="num">{tokenCount(
            u.tokens.cache_write)}</span> written to cache</>
      : <span className="muted">not recorded</span>}</dd>
    <dt>Time</dt>
    <dd>{u.duration_ms == null
      ? <span className="muted">not recorded</span>
      : <><b className="num">{runMinutes(u.duration_ms)}</b>
          {/* 🔴 AGAINST ITS LIMIT (2026-10-08): "20 turns" alone could not say whether a run
              stopped because it ran out. The limit comes from the run's own log. */}
          {turns != null && <> — {turnsOf(turns, r.run_log.limits?.max_turns)}</>}
          {u.duration_api_ms != null && <>; <span className="num">{runMinutes(
            u.duration_api_ms)}</span> of it waiting on the model, the rest on tools and the
            sandbox</>}</>}</dd>
    <dt>Subscription</dt>
    <dd>{gateway
      ? <span className="muted">none — a gateway model is paid per token and does not draw on
          the subscription</span>
      : weekly || five
        ? <>{weekly && <>weekly <b className="num">{weekly}</b></>}
            {weekly && five && " · "}
            {five && <>5-hour <b className="num">{five}</b></>} — read from the Claude CLI as the
            run went.{" "}
            {bounded
              ? <>Your own Claude use in the same minutes counts too, so this is at most what the
                  run used.</>
              : <>The window reset in between, so how far this run moved it cannot be read.</>}
          </>
        : <span className="muted">not read — graded before usage was recorded</span>}</dd>
    {gateway && u.gateway_usd != null && (<>
      <dt>Gateway spend</dt>
      <dd><Money usd={u.gateway_usd} /> <span className="muted">— measured at the gateway,
        paid per token, so it stays in dollars</span></dd></>)}
  </>);
}

/** Which language this mark was made in, how that was decided, and what a teacher can do.
 *
 * 🔴 THE CRITERIA ARE THE SAME FOR EVERY LANGUAGE; THIS SAYS WHICH CRAFT LINES WERE ADDED. The
 * console detects the language from the files at the graded commit, and the grader confirms or
 * flags it. When it is wrong, **Grade as…** records a teacher's choice for the next run and
 * **Run again** spends on it — two presses, because deciding and spending are two decisions and
 * two permissions.
 * ⚠️ The evidence is paths a student chose. It is shown here, to the teacher, and never went
 * into the document the model was marked with.
 */
function LanguageLine({ r, courseId, onChanged, part }: {
  r: Report; courseId: string; onChanged: () => void;
  /** 🔴 TWO HALVES IN TWO TABS (owner, 2026-10-13): what it was graded as stays on Overview;
   *  Grade as… — a decision about the next run, not a fact about this mark — is on Provenance,
   *  beside the language row it changes. */
  part: "facts" | "control";
}) {
  const lang = r.language;
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (!lang) return null;
  const ov = lang.override;
  const choose = (profile: string) => {
    setBusy(true); setMsg("");
    tracked(setSubmissionProfile(courseId, r.submission_id, profile))
      .then((res) => {
        setMsg(res.override
          ? "Saved. The next run grades it as that language; this mark stays as it is."
          : "Cleared. The next run detects the language from the files again.");
        setPick(""); onChanged();
      })
      .catch((e) => setMsg((e as ApiError).message || "could not reach the server"))
      .finally(() => setBusy(false));
  };
  const runAgain = () => {
    setBusy(true); setMsg("");
    tracked(queueGrade(courseId, r.course_work_id, [r.submission_id]))
      .then((res) => setMsg(res.note))
      .catch((e) => setMsg((e as ApiError).message || "could not reach the server"))
      .finally(() => setBusy(false));
  };
  if (part === "control") {
    if (!r.may_set_language && !ov && msg === "") return null;
    return (
      <div className="lang-ctl small" role="group" aria-label="Grade as">
      {ov && ov.profile !== lang.profile && ov.applies && (
          <div className="muted">The next run is graded as <strong>{ov.label}</strong> — set by{" "}
            {ov.set_by}{ov.set_at ? <> on <When iso={ov.set_at} /></> : null}.</div>)}
        {ov && !ov.applies && (
          <div className="warn">{ov.set_by}'s choice of {ov.label} lapsed: the student changed the
            link since, so the next run detects the language again.</div>)}
        {r.may_set_language && (
          <div className="row wrap gap" style={{ marginTop: 6 }}>
            <label htmlFor="grade-as">Grade as…</label>
            <select className="select" id="grade-as" value={pick} disabled={busy}
                    onChange={(e) => setPick(e.target.value)}>
              <option value="">choose a language</option>
              {lang.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <button className="btn sm" disabled={busy || pick === ""}
                    onClick={() => choose(pick)}>Save</button>
            {ov && (
              <button className="btn sm ghost" disabled={busy} onClick={() => choose("")}>
                Detect again</button>)}
            {r.may_run && ov && (
              <button className="btn sm" disabled={busy} onClick={runAgain}
                      title="Starts a paid run of this submission in the language chosen above.">
                Run again</button>)}
          </div>)}
        {msg !== "" && <div className="muted" role="status">{msg}</div>}
      </div>
    );
  }
  return (
    <div className="lang-line small" role="group" aria-label="Language">
      <div className="row wrap gap">
        <span>Graded as{" "}
          <strong>{lang.label || lang.profile || "an unrecorded language"}</strong>
          {lang.source === "teacher" ? " — set by a teacher"
            : lang.profile ? " — detected from the files" : ""}</span>
        {/* ⚠️ A SPACE BETWEEN THE PATHS, NOT A MARGIN: a margin parts them on screen and leaves
            the text "package.jsontsconfig.json" — what a screen reader says and a copy pastes. */}
        {lang.evidence.length > 0 && (
          <span className="muted">{lang.evidence.slice(0, 3).map((e, i) => (
            <span key={i}>{i > 0 ? " " : ""}<code>{e}</code></span>))}</span>)}
      </div>
      {/* 🔴 WHAT THE GRADER WAS HANDED BEYOND THIS REPOSITORY (2026-10-07): each submodule is its
          own repository, fetched at the commit this one pins, and a teacher checking the mark
          needs to open the same tree. */}
      {(r.repo.submodules ?? []).length > 0 && (
        <div className="muted">With {r.repo.submodules!.length === 1 ? "its submodule"
          : `its ${r.repo.submodules!.length} submodules`}, each at the commit this repository
          pins:{" "}{r.repo.submodules!.map((m, i) => (
            <span key={m.path}>{i > 0 ? ", " : ""}<a href={submoduleHref(m)} target="_blank"
              rel="noreferrer"><code>{m.path}</code></a></span>))}.</div>)}
    </div>
  );
}

/** Copies the message for the student, exactly as written — the plain text a teacher pastes.
 *  ⚠️ An icon with a name: `aria-label` says what it copies, and the result is said aloud. */
function CopyNote({ text }: { text: string }) {
  const [said, setSaid] = useState("");
  const copy = () => {
    const failed = () => setSaid("Could not copy — select the text instead");
    try {
      const w = navigator.clipboard?.writeText(text);
      if (!w) { failed(); return; }
      w.then(() => setSaid("Copied"), failed);
    } catch {
      failed();
    }
  };
  return (<>
    <span className="rp-copy-said" role="status">{said}</span>
    <button type="button" className="btn rp-copy" onClick={copy} title="Copy"
            aria-label="Copy the message for the student">
      <CopyGlyph />
    </button>
  </>);
}

export function SubmissionPage() {
  const { courseId } = useShell();
  const { sid } = useParams();
  return <SubmissionReport courseId={courseId} submissionId={sid ?? ""} />;
}

/** One part of the report below the criteria: a glass card with its own heading, which the rail
 *  links to by `id`. */
function Section({ id, title, sub, acts, children }: {
  id: string; title: string; sub?: ReactNode; acts?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="card rp-sec" id={id} aria-labelledby={`${id}-h`}>
      <header className="rp-sec-head">
        <h3 className="rp-sec-h" id={`${id}-h`}>{title}</h3>
        {sub && <div className="rp-sec-sub">{sub}</div>}
        {acts && <div className="rp-sec-acts">{acts}</div>}
      </header>
      <div className="rp-sec-body">{children}</div>
    </section>);
}

export function SubmissionReport({ courseId, submissionId, variant = "page", onDraft,
                                  onReport }: {
  courseId: string; submissionId: string;
  /** In a sheet the header carries the title and the way back, so the report draws neither. */
  variant?: "page" | "sheet";
  onDraft?: (d: DraftState) => void;
  /** The report as it arrives — the sheet's header names it from here when no row did. */
  onReport?: (r: Report) => void;
}) {
  // 🔴 THE SIDEBAR COUNTS COME FROM THE SHELL'S SHARED QUERY, AND NOTHING REFRESHED IT.
  // Releasing a grade moves it out of REVIEW, so the badge beside Grades was wrong until the
  // 60-second timer happened to fire — the one number on screen whose whole job is to say
  // there is work left.
  const { overview } = useShell();
  const { data, error, reload } = useAsync(
    () => getReport(courseId, submissionId), [courseId, submissionId]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof release>> | null>(null);
  const [relErr, setRelErr] = useState<ApiError | null>(null);
  // 🔴 A LINK THAT NAMES A PART OF THE REPORT LANDS ON IT. `#crit-C2` opened in a new tab — a
  // Ctrl-click on a rail row or on "Counted under C2" — arrives before the report does, and a
  // browser gives up looking for its fragment once the page has loaded, which is before this
  // fetch answers. So the first time the report is drawn, it goes there itself.
  // ⚠️ ONCE: a save reloads the report, and the reader is then at the review, not at whatever
  // the address named an hour ago.
  // 🔴 THE TABS ARE HELD HERE, ABOVE THE CRITERIA, which a saved review remounts: the reader
  // stays on the tab and the criterion they were on. It opens on the Overview.
  const [pane, setPane] = useState<Pane>("overview");
  const [crit, setCrit] = useState("");
  // 🔴 GOING TO A PART OF THE REPORT IS TWO STEPS: show its tab, THEN scroll to it — a browser
  // does not scroll to a hidden element. `hop` makes the second step run even when the tab was
  // already showing, where nothing else would change.
  const jump = useRef<string | null>(null);
  const [hop, setHop] = useState(0);
  const go = (id: string) => {
    const t = data ? targetOf(id, data.per_criterion) : null;
    if (!t) return;
    setPane(t.pane);
    if (t.crit) setCrit(t.crit);
    jump.current = id;
    setHop((n) => n + 1);
  };
  useLayoutEffect(() => {
    const id = jump.current;
    if (id === null) return;
    jump.current = null;
    document.getElementById(id)?.scrollIntoView?.({ block: "start" });
  }, [hop]);
  const { hash } = useLocation();
  const landed = useRef(false);
  useEffect(() => { if (data) onReport?.(data); }, [data, onReport]);
  useEffect(() => {
    if (!data || landed.current) return;
    landed.current = true;
    const id = safeDecode(hash.slice(1));
    if (id) go(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error) return <ErrorBox err={error} onRetry={reload} />;
  if (!data) return <Card><CardBody><div className="skel" /></CardBody></Card>;
  const r = data;

  const doRelease = (confirm: boolean) => {
    setBusy(true); setRelErr(null);
    tracked(release(courseId, submissionId, confirm))
      .then((res) => {
        if (confirm) { setPreview(null); reload(); overview.reload(); }
        else setPreview(res);
      })
      .catch((e) => setRelErr(e as ApiError))
      .finally(() => setBusy(false));
  };

  // 🔴 THE OVERVIEW OPENS THE REPORT (owner, 2026-10-13): which language it was marked in, what
  // needs a teacher's eye — flags and problems — then the two messages, then Release.
  const front = (<>
    {/* ⚠️ `.alert` IS A FLEX ROW: every bare text node and every <strong> in it becomes its
        own column. The prose lives in ONE `.body`, beside the icon — [X] seen in a render,
        where a notice came out as four cramped columns. */}
    {!r.gradeable && (
      <div className="alert refused small" role="alert">
        <span className="ico">⛔</span>
        <div className="body">
          <div className="t">HELD — infrastructure, not the student.</div>
          <div className="m">Releasing this would render our failure as their zero.</div>
        </div>
      </div>
    )}
    <LanguageLine r={r} courseId={courseId} onChanged={reload} part="facts" />
    {r.flags.length > 0 || r.problems.length > 0 ? (
      <div className="rp-pair">
        {/* 🔴 THE FLAGS REACHED NO SCREEN UNTIL 2026-09-27. The server has sent them since the
            first grade and `api.ts` typed them as strings; the prompt tells the model to raise
            `originality` "so the instructor notices it", and nothing showed it. A flag is the
            agent telling the teacher something the number cannot. */}
        {r.flags.length > 0 && (
          <Section id="rp-flags" title="Flags" sub="raised by the grader">
            <ul className="rp-sec-list rp-flags">
              {r.flags.map((f, i) => (
                <li key={i}>
                  <Badge tone={f.kind === "originality" ? "plain" : "warn"}>{flagLabel(f.kind)}</Badge>{" "}
                  {/* Inline Markdown, so `--ignore-keys` reads as code — never a link. */}
                  <span><MarkdownInline text={f.detail} links={false} /></span>
                  {f.suggested_profile
                    ? <span className="muted"> — the grader suggests {f.suggested_profile}</span>
                    : null}
                </li>))}
            </ul>
          </Section>)}
        {r.problems.length > 0 && (
          <Section id="rp-problems" title="Problems" sub="found by the console">
            <ul className="rp-sec-list">{r.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </Section>)}
      </div>
    ) : <p className="rp-quiet">Nothing was flagged, and the console found no problem with this
          grade.</p>}

    {/* 🔴 TWO AUDIENCES, NEVER MERGED, AND `student_note` NEVER FALLS BACK TO
        `teacher_note` — the second was produced after the agent read an
        adversarially-controlled repository. */}
    {r.student_note && (
      <Section id="rp-student" title="For the student"
               sub="plain text, so it pastes cleanly into Classroom"
               acts={<CopyNote text={r.student_note} />}>
        <div className="rp-read rp-student"><Markdown text={r.student_note} links={false} /></div>
      </Section>)}
    {r.teacher_note && (
      <Section id="rp-teacher" title="For the teacher"
               sub={"Written after the agent read a repository it does not control. "
                    + "Never paste this to a student unread."}>
        <div className="rp-read"><Markdown text={r.teacher_note} links={false} /></div>
      </Section>)}

    <Section id="rp-release" title="Release">
      {relErr && <ErrorBox err={relErr} />}
      {r.released ? (
        <p className="small">Released <When iso={r.released_at} /> by {r.released_by}.</p>
      ) : !r.may_release ? (
        <p className="small muted">You do not have <code>can_release</code> on this cohort.</p>
      ) : !r.write_allowed ? (
        <p className="small muted">This cohort is read-only, so nothing can be published to it.</p>
      ) : r.escalate && !r.review ? (
        /* A control you cannot use yet is disabled where it sits, with the reason. */
        <div className="row gap">
          <button className="btn" disabled>Preview release</button>
          <span className="small muted">Review this grade first — an escalated grade is
            released only with a review in force, and the reviewed total is what goes out.</span>
        </div>
      ) : (
        <div className="stack tight">
          {/* 🔴 NO BULK FORM, EVER. Each release is one person's decision about one student,
              and `confirm=false` is a preview that writes nothing. */}
          <div className="row gap">
            <button className="btn" disabled={busy} onClick={() => doRelease(false)}>
              Preview release</button>
            {/* 🔴 THE NUMBER ON THE BUTTON IS THE SERVER'S, from the preview — the same value
                the write that follows posts. */}
            {preview?.would_release && (
              <button className="btn primary" disabled={busy} onClick={() => doRelease(true)}>
                Publish {preview.would_release.points} to this student</button>)}
          </div>
          {preview?.would_release && (
            <div className="alert small">
              <div className="body">
                Nothing has been written yet. Publishing sets the mark <strong>and hands the
                submission back</strong>, so the student sees it immediately. There is no undo
                and no bulk form.
                {preview.would_release.reviewed_by && (<>
                  {" "}It posts the mark {preview.would_release.reviewed_by} reviewed — the
                  agent gave {preview.would_release.agent_points ?? DASH}.</>)}
              </div>
            </div>)}
        </div>
      )}
    </Section>
  </>);

  // Everything about the run: its own account first, then the commands, the log, our changes.
  const grader = (<>
    {/* 🔴 WHAT THE GRADER DID, which reached no screen until now. `build.error_summary` is
        its account of what it identified the project as, what it built, what it ran and
        what happened; `tests` is the commands it actually ran. Both are produced on every
        run and stored durably on the attempt, and neither was rendered anywhere — so "what
        did it actually do?" had no answer in the product. */}
    {r.agent_account?.summary && (
      <Section id="rp-grader" title="What the grader did" sub={<>
        {r.agent_account.outcome && (
          <Badge tone={r.agent_account.outcome === "ok" ? "ok" : "warn"}>
            build {r.agent_account.outcome}</Badge>)}
        {/* 🔴 WHAT IT USED, IN THE UNITS A SUBSCRIPTION IS SPENT IN (owner, 2026-10-03): its
            minutes, its tokens and how far the weekly figure moved — read by the formatters
            Runs and Grades use, so this run reads the same on all three. */}
        {(r.usage.num_turns ?? r.agent_account.turns) != null &&
          <span>{r.usage.num_turns ?? r.agent_account.turns} turns</span>}
        {(r.usage.duration_ms ?? (r.agent_account.duration_s != null
            ? r.agent_account.duration_s * 1000 : null)) != null &&
          <span>{runMinutes(r.usage.duration_ms
            ?? (r.agent_account.duration_s ?? 0) * 1000)}</span>}
        {r.usage.tokens && <span>{runTokens(tokenTotal(r.usage.tokens))} tokens</span>}
        {!r.usage.gateway && (moveRange(moveOf(r.usage.subscription, "seven_day"))
          ? <span title={resetDuring(moveOf(r.usage.subscription, "seven_day"))
                ? undefined : UPPER}>
              weekly {moveRange(moveOf(r.usage.subscription, "seven_day"))}</span>
          : <span title={NOT_READ_WHY}>{NOT_READ}</span>)}
      </>}>
        <div className="md sunk rp-sec-md">
          <Markdown text={r.agent_account.summary} links={false} />
        </div>
        {(r.agent_account.tests?.length ?? 0) > 0 && (
          <div className="tablewrap">
            <table className="mini">
              <thead><tr><th>Command it ran</th><th>Result</th><th>Detail</th></tr></thead>
              <tbody>
                {r.agent_account.tests!.map((t, i) => (
                  <tr key={i}>
                    <td><code>{t.name ?? DASH}</code></td>
                    <td><Badge tone={t.result === "pass" ? "ok" : "warn"}>
                      {t.result ?? "?"}</Badge></td>
                    <td className="muted">{t.assertion ?? ""}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>)}
        {/* 🔴 A SECOND TABLE, NOT MORE ROWS IN THE FIRST. The one above is what the agent
            EXECUTED and what happened; these are tests it would ADD and nothing ran them.
            Merging the two would put a row with no outcome under a Result column.
            ⚠️ NEVER SCORED, AND THE CAPTION SAYS SO — a model that writes its own exam marks
            its own homework, so these are a suggestion to a teacher and a student, and the
            mark above was decided without them. */}
        {(r.agent_account.suggested_tests?.length ?? 0) > 0 && (
          <div>
            <div className="small muted">
              Tests it would add — <strong>not scored</strong>, and nothing here ran.
            </div>
            <div className="tablewrap">
              <table className="mini">
                <thead><tr><th>Test</th><th>What it would check</th><th>Why</th></tr></thead>
                <tbody>
                  {r.agent_account.suggested_tests!.map((t, i) => (
                    <tr key={i}>
                      <td className="wrap"><code>{t.name ?? DASH}</code>
                        {t.tool && <div className="muted small">{t.tool}</div>}</td>
                      <td className="wrap">{t.what_it_checks ?? ""}</td>
                      <td className="wrap muted">{t.why ?? ""}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </div>)}
      </Section>)}

    {/* 🔴 HOW IT GOT THERE, STEP BY STEP (2026-10-08) — every tool call, every refusal, and
        how the run ended against its limits. Open when a step was refused or the run hit its
        turn limit, because that is when somebody is reading this to find out why. */}
    {(r.run_log.steps?.length ?? 0) > 0 && (
      <Section id="rp-log" title="Run log" sub={turnsOf(r.run_log.ended?.turns,
                                                        r.run_log.limits?.max_turns)}>
        <details className="rl-open"
                 open={(r.run_log.counts?.refused ?? 0) > 0 || !!r.run_log.ended?.at_limit}>
          <summary>{r.run_log.steps_total ?? r.run_log.steps!.length} steps
            {(r.run_log.counts?.refused ?? 0) > 0 && <> · {r.run_log.counts!.refused} refused</>}
          </summary>
          <RunLogView log={r.run_log} />
        </details>
      </Section>)}

    {/* ⚠️ OUR side, never the student's — a fix here is the harness being wrong. */}
    {(r.fixes_applied?.length ?? 0) > 0 && (
      <Section id="rp-changes" title="What it changed on our side"
               sub="The harness, never the student's work.">
        <ul className="rp-sec-list">
          {r.fixes_applied.map((f, i) => (
            <li key={i}><b>{f.what ?? ""}</b>
              {f.why ? <> — <span className="muted">{f.why}</span></> : null}</li>))}
        </ul>
      </Section>)}

    {!r.agent_account?.summary && (r.run_log.steps?.length ?? 0) === 0
      && (r.fixes_applied?.length ?? 0) === 0 && (
      <p className="rp-quiet">Nothing was recorded about how this grade was made.</p>)}
  </>);

  const provenance = (<>
    <Section id="rp-provenance" title="Provenance">
      <dl className="kv">
        <dt>Engine</dt><dd><code>{String(r.grader.engine)}</code> via <code>{String(r.grader.route)}</code>
          {r.grader.route === "anthropic" && <> — the subscription</>}
          {r.usage.gateway && <> — the gateway</>}</dd>
        <dt>Model observed</dt><dd><code>{(r.grader.review_models as string[])?.join(", ") || "—"}</code></dd>
        {/* 🔴 USAGE, NOT A PRICE (owner, 2026-10-03). The Cost row printed what the work would
            have cost at API prices — a price nobody pays on a subscription. What a grading
            spends is tokens, time and the subscription's own allowance; the cost columns are
            still stored, and not shown. */}
        <UsageRows r={r} />
        <dt>Attribution</dt>
        <dd>
          <State s={r.attribution.verdict === "verified" ? "OK" : "WARN"} />{" "}
          {r.attribution.verdict}
          {/* 🔴 A VALIDITY FIELD, NOT AN ACCOUNTING ONE. A cached re-run reproduces
              `modelUsage` perfectly, so a "second opinion" served from the gateway's
              response cache is a replay presented as corroboration. */}
          {r.attribution.cache_hit === true && (
            <span className="badge stop" title="Served from the gateway's response cache.
A re-run that hits the cache reproduces the previous answer byte for byte, so it is a replay,
not a second opinion.">cache hit</span>)}
        </dd>
        <dt>Fingerprint</dt>
        <dd>{r.grader.fingerprint_complete
          ? <Badge tone="ok">complete</Badge>
            : <Badge tone="warn" title="One of the seven fingerprint inputs is missing, so this grade cannot be compared with another.">
                incomplete</Badge>}
          {" "}<code className="small">{String(r.grader.runner_version)}</code></dd>
        {/* ⚠️ THE FINGERPRINT'S OWN INPUTS. `fingerprint_complete` above is a verdict about
            these seven values; showing the verdict without the values makes an incomplete
            fingerprint impossible to diagnose. */}
        <dt>Language</dt>
        <dd className="small">
          {r.language?.profile
            ? <>{r.language.label || r.language.profile} v{r.language.version} ·{" "}
                {r.language.source === "teacher" ? "set by a teacher" : "detected"}</>
            : <span className="muted">not recorded — graded before languages were detected</span>}
          <LanguageLine r={r} courseId={courseId} onChanged={reload} part="control" />
        </dd>
        <dt>Built by</dt>
        <dd className="small">
          {String(r.grader.console_commit ?? "")
            ? <>console <code>{String(r.grader.console_commit).slice(0, 7)}</code></>
            : <span className="muted">a console build CI did not make</span>}
        </dd>
        <dt>Rubric · suite · prompt</dt>
        <dd className="small">
          <code>{String(r.grader.rubric_sha256 ?? "").slice(0, 12) || DASH}</code> ·{" "}
          <code>{String(r.grader.suite_sha ?? "").slice(0, 12) || DASH}</code> ·{" "}
          <code>{String(r.grader.prompt_sha256 ?? "").slice(0, 12) || DASH}</code>
          {r.grader.sanitiser_version ?
            <> · sanitiser {String(r.grader.sanitiser_version)}</> : null}
        </dd>
        {/* Present even when empty: it is what proves the sandbox boundary was observed. */}
        <dt>Sandbox refusals</dt>
        <dd>{r.permission_denials.length === 0
          ? <span className="muted">none — the agent asked for nothing outside its jail</span>
          : <>{r.permission_denials.length} denied
              <ul className="small">{r.permission_denials.map((d, i) =>
                <li key={i}><code>{d.tool_name}</code>{" "}
                  {JSON.stringify(d.tool_input).slice(0, 140)}</li>)}</ul></>}</dd>
        {r.attempt && (<>
          <dt>Durable accept</dt>
          <dd className="small">envelope committed <When iso={r.attempt.envelope_received_at} />,
              processed <When iso={r.attempt.processed_at} /> · job{" "}
              <code>{r.attempt.wm_job_id}</code></dd></>)}
      </dl>
    </Section>

    {/* 🔴 THE OVERRIDE ORACLE, AND IT WAS ON THE WIRE AND RENDERED NOWHERE. This is the
        record of what actually reached Google: a draft grade, an assigned grade, a return,
        or a mark somebody typed in by hand. A number on this page is a claim; this table is
        the evidence that a student was ever shown it. */}
    <Section id="rp-classroom" title="What reached Classroom">
      {r.classroom_writes.length === 0
        ? <p className="small muted">Nothing. No grade, release or announcement for this
            submission has been written to Classroom by this console.</p>
        : <div className="tablewrap">
            <table className="mini">
              <thead><tr><th>Write</th><th className="num">Points</th><th>Status</th>
                         <th>By</th><th>When</th></tr></thead>
              <tbody>
                {r.classroom_writes.map((w, i) => (
                  <tr key={i}>
                    <td><code>{w.kind}</code></td>
                    <td className="num">{w.points ?? DASH}</td>
                    <td><State s={w.status} />
                      {w.error && <div className="small warn">{w.error}</div>}</td>
                    <td className="small">{w.actor}</td>
                    <td className="small"><When iso={w.at} /></td>
                  </tr>))}
              </tbody>
            </table>
          </div>}
    </Section>

  </>);

  const sheet = variant === "sheet";
  return (
    <div className={sheet ? "rp-page rp-in-sheet" : "rp-page"}>
      <header className="rp-top">
        {/* ⚠️ WHERE YOU CAME FROM, BOTH WAYS: the assignment, and its Handed in list filtered to
            the state this grade is in. Since 2026-10-13 no list spans the cohort, so the second
            link is the assignment's own, never "every grade in review" everywhere. In a sheet
            the list is right behind it. */}
        {!sheet && <nav className="back rp-back" aria-label="Back">
          <Link to={routes.assignment(courseId, r.course_work_id)}>
            ← {r.assignment_title || r.course_work_id}</Link>
          {FILTER_OF_STATUS[r.status] && (
            <Link to={routes.assignment(courseId, r.course_work_id, "handed",
                                        FILTER_OF_STATUS[r.status])}>
              ← Every grade {FILTER_NOUN[FILTER_OF_STATUS[r.status]!]} here</Link>)}
        </nav>}
        <div className="rp-top-main">
          <div className="rp-top-text">
            {!sheet && <>
              <span className="eyebrow">Grade report</span>
              <h2 className="rp-title">
                {r.student || "Unnamed student"}
                <span className="rp-title-of">{" — "}{r.assignment_title || r.course_work_id}</span>
              </h2>
            </>}
            <p className="rp-top-meta">Graded <When iso={r.graded_at} />
              {r.language?.label ? <> · as <strong>{r.language.label}</strong></> : null}
              {" "}· base <code>{r.assignment}</code>
              {Number(r.grader.rubric_version) > 0 ? <> v{String(r.grader.rubric_version)}</> : null}
              {" "}· <code>{r.submission_id}</code></p>
          </div>
          <div className="rp-top-acts">
            {r.repo.url && <a className="btn sm" href={treeHref(r.repo)} target="_blank"
                              rel="noreferrer"
                              title={r.repo.commit ? `commit ${r.repo.commit}` : undefined}>
              {r.repo.commit ? <>Graded commit <code>{r.repo.commit.slice(0, 7)}</code></>
                             : "Repository"}</a>}
            {r.alternate_link && <a className="btn sm" href={r.alternate_link} target="_blank"
                                    rel="noreferrer">Open in Classroom</a>}
          </div>
        </div>
      </header>
      {/* 🔴 A SAVED REVIEW DROPS THE RELEASE PREVIEW, at once and not when the report comes
          back: the preview named the mark of the review it was made from, the save moved that
          mark on the server, and until the reload lands the old button would post the new
          number under the old one's label. */}
      <Criteria key={`${r.graded_at ?? ""}|${r.review?.id ?? ""}`} r={r} courseId={courseId}
                onSaved={() => { setPreview(null); reload(); overview.reload(); }}
                onDraft={onDraft} panes={{ overview: front, grader, provenance }} pane={pane}
                crit={crit} go={go} select={setPane} pick={setCrit} />
    </div>
  );
}
